import constant from 'lodash/constant'
import { useMemo } from 'react'
import { Badge } from '@klear/ui/components/badge'
import { moneySumFormatted } from '@klear/order-event/list-display'
import {
  isInvoiceOfferFilePreviewContext,
  type OrderEventFilePreviewBreadcrumbItem,
  type OrderEventFilePreviewContext
} from '@klear/order-event/file-preview-display'
import { trpc } from '@klear/ui/lib/trpc'

type BreadcrumbProps = {
  context: OrderEventFilePreviewContext
  getEventUrl?: (item: OrderEventFilePreviewBreadcrumbItem) => string
}

export function OrderEventFilePreviewBreadcrumb({ context, getEventUrl = constant('') }: BreadcrumbProps) {
  const { order, ancestors } = context

  return (
    <div className="mb-4 flex items-center text-sm gap-6">
      <div className="flex items-center gap-2">
        <Badge variant="outline">{order.idDisplay}</Badge>
        <div className="text-muted-foreground">{order.displayName}</div>
      </div>

      {ancestors.map((item) => {
        const eventUrl = getEventUrl(item)
        const label = `${item.typeLabel}${item.number ? ` #${item.number}` : ''}`
        return eventUrl ? (
          <a key={item.id} href={eventUrl} className="flex items-center gap-2 hover:opacity-80">
            <Badge variant="outline">{item.idDisplay}</Badge>
            <span className="text-muted-foreground">{label}</span>
          </a>
        ) : (
          <div key={item.id} className="flex items-center gap-2">
            <Badge variant="outline">{item.idDisplay}</Badge>
            <span className="text-muted-foreground">{label}</span>
          </div>
        )
      })}
    </div>
  )
}

export function OrderEventFilePreviewEventDetails({ context }: { context: OrderEventFilePreviewContext }) {
  const { details } = context

  const { data: offerFees = [] } = trpc.fee.list.useQuery(
    { filters: { parentEventId: [context.eventId] } },
    { enabled: isInvoiceOfferFilePreviewContext(context), staleTime: 60_000 }
  )

  const invoiceOfferFeeFormatted = useMemo(() => {
    if (!isInvoiceOfferFilePreviewContext(context)) return null
    return moneySumFormatted(offerFees.map((fee) => fee.amount)) ?? '—'
  }, [context, offerFees])

  const feeLabel = details.feeFormatted ?? invoiceOfferFeeFormatted

  return (
    <div className="bg-card p-4 rounded-lg shadow-sm">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-2 flex flex-wrap items-center gap-2">
          {details.typeLabel}
          <Badge variant="outline">{context.idDisplay}</Badge>
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {details.effectiveDate != null && (
            <>
              <div className="text-sm text-muted-foreground">Effective Date:</div>
              <div>{details.effectiveDate}</div>
            </>
          )}
          {details.number != null && details.number !== '' && (
            <>
              <div className="text-sm text-muted-foreground">Number:</div>
              <div>{details.number}</div>
            </>
          )}
          {details.amountFormatted != null && (
            <>
              <div className="text-sm text-muted-foreground">Amount:</div>
              <div>{details.amountFormatted}</div>
            </>
          )}
          {details.endDate != null && (
            <>
              <div className="text-sm text-muted-foreground">
                {context.eventType === 'purchase_order' ? 'Target Delivery Date:' : 'End Date:'}
              </div>
              <div>{details.endDate}</div>
            </>
          )}
          {details.advanceRateFormatted != null && (
            <>
              <div className="text-sm text-muted-foreground">Advance Rate:</div>
              <div>{details.advanceRateFormatted}</div>
            </>
          )}
          {feeLabel != null && (
            <>
              <div className="text-sm text-muted-foreground">Fee:</div>
              <div>{feeLabel}</div>
            </>
          )}
          {details.settlementCollectionFormatted != null && (
            <>
              <div className="text-sm text-muted-foreground">Settlement Collection:</div>
              <div>{details.settlementCollectionFormatted}</div>
            </>
          )}
          {details.settlementAdvanceRecoveryFormatted != null && (
            <>
              <div className="text-sm text-muted-foreground">Advance Recovery:</div>
              <div>{details.settlementAdvanceRecoveryFormatted}</div>
            </>
          )}
          {details.settlementNetAmountFormatted != null && (
            <>
              <div className="text-sm text-muted-foreground">Net Amount:</div>
              <div>{details.settlementNetAmountFormatted}</div>
            </>
          )}
        </div>
      </div>
      {details.notes != null && details.notes.trim() !== '' && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Notes</h3>
          <div className="text-sm whitespace-pre-wrap">{details.notes}</div>
        </div>
      )}
    </div>
  )
}
